import React, { useState, useEffect } from 'react';
import { getFaqs, deleteFaq, getFaqCategories } from '../../services/faqsService';
import { useToast } from '../../contexts/ToastContext';
import Loader from '../../components/common/Loader';
import Table from '../../components/common/Table';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { Link } from 'react-router-dom';
import { PlusCircleIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

function FaqListPage() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    is_active: ''
  });
  const [categories, setCategories] = useState([]);
  const { showSuccess, showError, showWarning } = useToast();

  useEffect(() => {
    fetchFaqs();
    fetchCategories();
  }, []);

  useEffect(() => {
    // Debounced search when filters change
    const timeoutId = setTimeout(() => {
      if (pagination.page === 1) {
        fetchFaqs();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters]);

  useEffect(() => {
    // Fetch when page changes
    fetchFaqs();
  }, [pagination.page]);

  const fetchFaqs = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('🔍 Fetching FAQs with:', { filters, page: pagination.page, per_page: pagination.per_page });
      
      const response = await getFaqs(filters, pagination.page, pagination.per_page);
      console.log('📋 FAQ response:', response);
      
      if (response.success && response.data) {
        setFaqs(response.data);
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.pagination.total,
            pages: response.pagination.total_pages || response.pagination.pages
          }));
        }
      } else if (Array.isArray(response.data)) {
        // Handle direct array response (fallback)
        setFaqs(response.data);
      } else {
        // Handle direct array response (legacy)
        const faqsArray = Array.isArray(response) ? response : [];
        setFaqs(faqsArray);
      }
    } catch (err) {
      setError('Failed to load FAQs: ' + err.message);
      showError('Failed to load FAQs: ' + err.message);
      console.error('❌ FAQ fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getFaqCategories();
      console.log('📂 Categories response:', response);
      
      // Handle success_response format from backend
      if (response.success && response.data) {
        setCategories(Array.isArray(response.data) ? response.data : []);
      } else if (Array.isArray(response)) {
        // Handle direct array response (fallback)
        setCategories(response);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error('❌ Failed to load categories:', err);
      setCategories([]);
      // Don't show error for categories as it's not critical
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this FAQ?')) {
      try {
        await deleteFaq(id);
        showSuccess('FAQ deleted successfully!');
        await fetchFaqs(); // Refresh the list
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to delete FAQ';
        setError(errorMessage);
        showError(errorMessage);
        console.error('❌ Delete FAQ error:', err);
      }
    }
  };

  const handleFilterChange = (field, value) => {
    console.log('🔍 Filter change:', { field, value });
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      is_active: ''
    });
  };

  const headers = ['Question', 'Category', 'Active', 'Order', 'Actions'];

  const renderRow = (faq) => (
    <tr key={faq.id} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-green-50 transition-all duration-200">
      <td className="px-6 py-4 text-sm font-medium text-gray-900">{faq.question}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium capitalize">
          {faq.category || 'N/A'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {faq.is_active ? (
          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 mt-0.5"></div>
            Active
          </span>
        ) : (
          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border border-red-200">
            <div className="w-2 h-2 bg-red-500 rounded-full mr-2 mt-0.5"></div>
            Inactive
          </span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-lg text-xs font-medium">
          {faq.display_order}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
        <Link 
          to={`/faqs/edit/${faq.id}`} 
          className="inline-flex items-center text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-3 py-1 rounded-lg transition-all duration-200"
        >
          <PencilIcon className="h-4 w-4 mr-1" />
          Edit
        </Link>
        <button 
          onClick={() => handleDelete(faq.id)} 
          className="inline-flex items-center text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1 rounded-lg transition-all duration-200"
        >
          <TrashIcon className="h-4 w-4 mr-1" />
          Delete
        </button>
      </td>
    </tr>
  );

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative m-6 shadow-md" role="alert">
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="p-2 bg-gray-50 min-h-screen">
      <div className="w-full">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center px-2">
          <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
          FAQs Management
        </h2>

      {/* Filters Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-lg p-6 mb-6 border border-blue-200 hover:shadow-xl transition-shadow duration-300 mx-2">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
          Search & Filter
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <Input
              label="Search FAQs"
              placeholder="Search by question or answer..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              icon={<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />}
              className="border-blue-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="shadow-sm border border-blue-200 rounded-lg w-full py-3 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.category} value={cat.category}>
                  {cat.category} ({cat.faq_count})
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">Status</label>
            <select
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
              className="shadow-sm border border-blue-200 rounded-lg w-full py-3 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">&nbsp;</label>
            <Button
              type="button"
              variant="secondary"
              onClick={clearFilters}
              className="w-full py-3 px-4 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {/* FAQs Table Section */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-lg p-6 mb-6 border border-green-200 hover:shadow-xl transition-shadow duration-300 mx-2">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
              All FAQs
            </h3>
            {pagination.total > 0 && (
              <p className="text-sm text-gray-600 mt-2 bg-white px-3 py-1 rounded-lg border border-green-200">
                Showing <span className="font-medium text-green-700">{((pagination.page - 1) * pagination.per_page) + 1}</span> to <span className="font-medium text-green-700">{Math.min(pagination.page * pagination.per_page, pagination.total)}</span> of <span className="font-medium text-green-700">{pagination.total}</span> FAQs
              </p>
            )}
          </div>
          <Link to="/faqs/new">
            <Button variant="primary" className="inline-flex items-center py-3 px-6 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600">
              <PlusCircleIcon className="h-5 w-5 mr-2" /> Add New FAQ
            </Button>
          </Link>
        </div>
        
        {faqs.length > 0 ? (
          <Table headers={headers} data={faqs} renderRow={renderRow} />
        ) : (
          <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-green-50 rounded-xl border-2 border-dashed border-green-200">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-gray-500 text-lg mb-2 font-medium">
              {loading ? 'Loading FAQs...' : 'No FAQs found'}
            </div>
            <p className="text-gray-400 mb-6">
              {Object.values(filters).some(f => f) ? 
                'Try adjusting your filters or create a new FAQ.' : 
                'Get started by creating your first FAQ.'
              }
            </p>
            {!Object.values(filters).some(f => f) && (
              <Link to="/faqs/new">
                <Button variant="primary" className="inline-flex items-center">
                  <PlusCircleIcon className="h-5 w-5 mr-2" />
                  Create Your First FAQ
                </Button>
              </Link>
            )}
          </div>
        )}
        
        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4">
            <div className="text-sm text-gray-700 bg-white px-3 py-2 rounded-lg border border-green-200 shadow-sm">
              <span className="font-medium">Page</span> <span className="text-green-600 font-bold">{pagination.page}</span> <span className="font-medium">of</span> <span className="text-green-600 font-bold">{pagination.pages}</span>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-4 py-2 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200"
              >
                Previous
              </Button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                const pageNum = Math.max(1, pagination.page - 2) + i;
                if (pageNum > pagination.pages) return null;
                
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === pagination.page ? "primary" : "secondary"}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200 ${
                      pageNum === pagination.page 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg' 
                        : 'hover:bg-green-50'
                    }`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              
              <Button
                variant="secondary"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="px-4 py-2 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default FaqListPage;